import {useEffect, useState} from 'react';

import LocalStorage from 'web-app/services/local-storage';
import {hasValue} from '@famly/stat_ts-utils_has-value';

/**
 * Use this to store and manage encrypted values in local storage.
 * Values can be stored under keys that can be created to be unique
 * to the shape of values `<T>`.
 *
 * - `useOfflineDrafting`: Hook that is used to "watch" values and store
 * them to local storage when they change.
 * - `getOfflineValues`: Function to get stored values.
 * - `clearValues`: Function to remove stored values.
 * - `hasOfflineValues`: Function to check if any stored values exists - This is much cheaper than `getOfflineValues`
 *
 * Beware that values saved with this hook needs to be JSON friendly. That means
 * you _cannot_ have instances of classes, Immutable instances, etc. saved and
 * expect it to work. To work around this you can use the `encode` and `decode`
 * callbacks, which should transform `T` to a JSON friendly value and back again.
 *
 * @example
 * const keygen = (foo: {bar: string; fizz: string}) => `${foo.bar}_${foo.fizz}`;
 * const {useOfflineDrafting, getOfflineValues, clearValues} = createOfflineDrafter<SomeValue>()({keygen});
 * const valueToKeygen = (value: SomeValue) => ({foo: value.xx, fizz: value.yy});
 *
 * const MyComponent = () => {
 *   const initialValues = getOfflineValues({bar: 'abc', fizz: 'def'});
 *   ...
 *   useOfflineDrafting(someValue, valueToKeygen, {skip: someCondition});
 * }
 *
 * const handleDiscard = () => {
 *   clearValues({bar: 'abc', fizz: 'def'});
 * }
 *
 */
export const createOfflineDrafter =
    <T>() =>
    <U, K extends string>({
        keygen,
        decode,
        encode,
    }: {keygen: (value: T) => K} & (
        | {encode?: undefined; decode?: undefined}
        | {encode: (value: T) => U; decode: (value: U) => T}
    )) => {
        const useOfflineDrafting = (
            value: T,
            options?: {
                skip?: boolean;
            },
        ) => {
            const [key, setKey] = useState(() => keygen(value));
            const {skip} = options || {};

            // Remove local storage entry from old key when changing
            useEffect(() => {
                if (skip) {
                    return;
                }
                const newKey = keygen(value);
                if (key === newKey) {
                    return;
                }
                setKey(newKey);
                LocalStorage.clearForUser(key);
            }, [key, skip, value]);

            // Add local storage entry on value or key change
            useEffect(() => {
                if (skip) {
                    return;
                }
                saveValues(value);
            }, [skip, value]);
        };

        const hasOfflineValues = (key: K) => LocalStorage.existsForUser(key);

        const getOfflineValues = (key: K) => {
            const localValue = LocalStorage.getForUser(key, false);
            if (!hasValue(localValue)) {
                return null;
            }
            try {
                const parsedEntry = JSON.parse(localValue);
                return hasValue(decode) ? decode(parsedEntry) : (parsedEntry as T);
            } catch {
                return null;
            }
        };

        const clearValues = (key: K) => {
            LocalStorage.clearForUser(key);
        };

        const saveValues = (value: T) => {
            const key = keygen(value);
            const entry = JSON.stringify(hasValue(encode) ? encode(value) : value);
            LocalStorage.saveForUser(key, entry, false);
        };

        return {useOfflineDrafting, getOfflineValues, clearValues, hasOfflineValues, saveValues};
    };
